import FiltersAllGoal from "./FiltersAllGoal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { GoalItem } from "../types";
import {
  formatGoalStatus,
  getCapitalizeWord,
  getCreatedAtDate,
  getGoalProgress,
  getGoalStatus,
} from "../helpers";
import AvatarCircle from "app/components/AvatarCircle";
import { Dropdown } from "react-bootstrap";
import { VISIBILITY_ICONS } from "../constants";
import ReflectionProgressBar from "../Reflection/ReflectionProgressBar";
import { Separator } from "app/shadcnComponents/ui/separator";
import { faBoxArchive } from "@fortawesome/pro-regular-svg-icons";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";

interface Props {
  goals: GoalItem[];
  onEdit: (goalItem: GoalItem) => void;
  department: string;
  onDepartmentChange: (v: string) => void;
  status: string;
  onStatusChange: (v: string) => void;
  visibility: string;
  onVisibilityChange: (v: string) => void;
  error?: string;
  isLoading?: boolean;
  onArchive: (goalId: number) => void;
  onUnarchive: (goalId: number) => void;
  onDelete: (goalId: number) => void;
}

export default function GoalsTable({
  goals,
  onEdit,
  department,
  onDepartmentChange,
  status,
  onStatusChange,
  visibility,
  onVisibilityChange,
  error,
  isLoading = false,
  onArchive,
  onUnarchive,
  onDelete,
}: Readonly<Props>) {
  const navigate = useNavigate();

  const getEmployeeCell = (user: GoalItem["user"]) => {
    const fullName =
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "Unknown";
    const role = user?.jobTitle ?? "";

    return (
      <div className="user-cell">
        <AvatarCircle userAccountId={user?.userAccountId} />

        <div className="user-info">
          <div className="name">{fullName}</div>
          {role && <div className="role">({role})</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="goals-table-wrapper">
      <Separator className="my-[8px]" />
      <FiltersAllGoal
        department={department}
        onDepartmentChange={onDepartmentChange}
        status={status}
        onStatusChange={onStatusChange}
        visibility={visibility}
        onVisibilityChange={onVisibilityChange}
      />
      <table className="goals-table">
        <thead>
          <tr>
            <th style={{ width: "18%" }}>Employee</th>
            <th style={{ width: "22%" }}>Goal</th>
            <th style={{ width: "18%" }}>Links To</th>
            <th>Due</th>
            <th>Progress / Status</th>
            <th>Visibility</th>
          </tr>
        </thead>

        <tbody>
          {/* Inline loader while filtering */}
          {isLoading && (
            <tr className="loading-row">
              {Array.from({ length: 7 }).map((_, index) => (
                <td key={index}>
                  <div className="table-loader">
                    <Skeleton className="h-4 w-full" />
                  </div>
                </td>
              ))}
            </tr>
          )}

          {/* Error */}
          {!isLoading && error && goals.length === 0 && (
            <tr className="error-row">
              <td colSpan={7}>
                <div className="error-message">{error}</div>
              </td>
            </tr>
          )}

          {/* No data */}
          {!isLoading && !error && goals.length === 0 && (
            <tr className="no-data-row">
              <td colSpan={7}>No data found</td>
            </tr>
          )}

          {/* Data rows */}
          {!isLoading &&
            goals.map((goal) => {
              const status = getGoalStatus(goal);
              const progressPercent = getGoalProgress(goal);

              return (
                <tr key={goal.goalId}>
                  <td className="goal-title">{getEmployeeCell(goal.user)}</td>
                  {/* GOAL */}
                  <td className="goal-title">{goal.title}</td>

                  {/* LINKS TO */}
                  <td>
                    {goal.parentGoal ? goal.parentGoal.title : "— Not linked"}
                  </td>

                  {/* DUE */}
                  <td>{getCreatedAtDate(goal.dueDateFormatted)}</td>

                  {/* PROGRESS */}
                  <td>
                    {status && progressPercent !== null ? (
                      <div className="progress-cell">
                        <ReflectionProgressBar
                          reflectionStatus={status}
                          progressPercent={progressPercent}
                        />

                        <span className={`status-badge ${status}`}>
                          <span className="dot" />
                          {formatGoalStatus(status)}
                        </span>
                      </div>
                    ) : (
                      <span className="no-progress">—</span>
                    )}
                  </td>

                  {/* VISIBILITY */}
                  <td>
                    <span className={`goal-visibility-text ${goal.visibility}`}>
                      <FontAwesomeIcon
                        icon={VISIBILITY_ICONS[goal.visibility]}
                        className="me-2"
                      />
                      {getCapitalizeWord(goal.visibility)}
                    </span>
                  </td>
                  {/* ACTION */}
                  <td className="actions-cell" style={{ position: "relative" }}>
                    <Dropdown
                      onSelect={(e) => {
                        switch (e) {
                          case "edit":
                            onEdit(goal);
                            break;
                          case "viewProfile":
                            navigate(`/UserGuide/${goal.user?.userAccountId}`);
                            break;
                          case "archive":
                            onArchive(goal.goalId);
                            break;
                          case "unarchive":
                            onUnarchive(goal.goalId);
                            break;
                          case "delete":
                            onDelete(goal.goalId);
                            break;
                        }
                      }}
                    >
                      <Dropdown.Toggle
                        variant="outline-primary"
                        id="dropdown-basic"
                        className="no-caret"
                      >
                        <FontAwesomeIcon icon="ellipsis" />
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        className="flip"
                        renderOnMount
                        popperConfig={{ strategy: "fixed" }}
                      >
                        {goal.archived ? (
                          <>
                            <Dropdown.Item eventKey="unarchive">
                              Unarchive
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="delete" className="danger">
                              Delete
                            </Dropdown.Item>
                          </>
                        ) : (
                          <>
                            <Dropdown.Item eventKey="edit">
                              <FontAwesomeIcon icon={faEdit} />
                              Edit Goal
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="viewProfile">
                              <FontAwesomeIcon icon={faEye} />
                              View Profile
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item eventKey="archive">
                              <FontAwesomeIcon icon={faBoxArchive} />
                              Archive
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
